import { useEffect, useState } from "react";
import {
  NavLink,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import {
  Database,
  History,
  Grid3x3,
  Activity,
  LogOut,
  ShieldCheck,
} from "lucide-react";
import Logo from "../components/Logo";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import AdminListino from "./admin/AdminListino";
import AdminSessions from "./admin/AdminSessions";
import AdminMatrice from "./admin/AdminMatrice";
import AdminHealth from "./admin/AdminHealth";

const ITEMS = [
  { to: "/admin/listino", label: "Listino", icon: Database },
  { to: "/admin/sessions", label: "Sessioni", icon: History },
  { to: "/admin/matrice", label: "Matrice", icon: Grid3x3 },
  { to: "/admin/health", label: "Stato", icon: Activity },
];

export default function Admin() {
  const [auth, setAuth] = useState({ checking: true, authenticated: false });
  const navigate = useNavigate();

  useEffect(() => {
    api
      .adminWhoami()
      .then((d) => setAuth({ checking: false, authenticated: !!d.authenticated }))
      .catch(() => setAuth({ checking: false, authenticated: false }));
  }, []);

  const logout = async () => {
    try {
      await api.adminLogout();
    } catch (e) {
      // ignore
    }
    navigate("/admin/login", { replace: true });
  };

  if (auth.checking) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-sm text-slate-500">Verifica accesso...</div>
      </div>
    );
  }
  if (!auth.authenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-shell">
      <header
        className="sticky top-0 z-40 bg-white"
        style={{ borderBottom: "1px solid var(--border-subtle)" }}
      >
        <div className="max-w-[1400px] mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Logo size={28} />
            <div className="leading-tight flex items-baseline gap-2">
              <span
                className="font-semibold tracking-tight text-[15px]"
                style={{ color: "var(--text-primary)" }}
              >
                TechQuote
              </span>
              <span
                className="tq-section-label flex items-center gap-1"
              >
                <ShieldCheck className="h-3 w-3" />
                Admin
              </span>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={logout}
            className="rounded-md text-slate-600 hover:text-slate-900 gap-2"
            data-testid="admin-logout"
          >
            <LogOut className="h-3.5 w-3.5" />
            Esci
          </Button>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 py-6 grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6">
        <aside className="space-y-1">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">
            Console
          </div>
          {ITEMS.map((it) => {
            const Icon = it.icon;
            return (
              <NavLink
                key={it.to}
                to={it.to}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-3 h-9 rounded-md text-[13px] font-medium transition-colors ${
                    isActive
                      ? "text-white"
                      : "hover:bg-slate-100"
                  }`
                }
                style={({ isActive }) => ({
                  background: isActive ? "var(--accent-primary)" : "transparent",
                  color: isActive ? "#FFFFFF" : "var(--text-secondary)",
                })}
                data-testid={`admin-nav-${it.label.toLowerCase()}`}
              >
                <Icon className="h-4 w-4" />
                {it.label}
              </NavLink>
            );
          })}
        </aside>

        <section>
          <Routes>
            <Route index element={<Navigate to="/admin/listino" replace />} />
            <Route path="listino" element={<AdminListino />} />
            <Route path="sessions" element={<AdminSessions />} />
            <Route path="matrice" element={<AdminMatrice />} />
            <Route path="health" element={<AdminHealth />} />
          </Routes>
        </section>
      </div>
    </div>
  );
}
